import { Router, RouterModule } from '@angular/router';
import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { DialogService } from 'src/app/services/dialog.service';
import { ContentService } from 'src/app/services/content.service';
import { formatError } from 'src/app/utils/errorMessage.formatter';
import { ContentList } from 'src/app/models/content/content-list.model';
import { ListParams } from 'src/app/models/list-params/list-params.model';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';
import { HeaderComponent } from 'src/app/shared/header/header.component';
import { LoaderComponent } from 'src/app/shared/loader/loader.component';
import { SpinnerModule } from 'src/app/shared/spinner/spinner.module';
import { ListViewModule } from 'src/app/shared/list/list-view/list-view.module';
import { TextfieldComponent } from 'src/app/shared/form/textfield/textfield.component';
import { ListToolbarModule } from 'src/app/shared/list/list-toolbar/list-toolbar.module';
import { ListPaginatorModule } from 'src/app/shared/list/list-paginator/list-paginator.module';
import { CopyToClipboardModule } from 'src/app/shared/copy-to-clipboard/copy-to-clipboard.module';

@Component({
  selector: 'app-content-list',
  templateUrl: './content-list.component.html',
  styleUrls: ['./content-list.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '20px', whiteSpace: 'nowrap' })),
      state('expanded', style({ height: '*', whiteSpace: 'normal' })),
      transition('collapsed <=> expanded', animate('.4s ease-in-out')),
    ]),
  ],
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    LoaderComponent,
    SpinnerModule,
    ListViewModule,
    TextfieldComponent,
    ListToolbarModule,
    ListPaginatorModule,
    CopyToClipboardModule,
  ],
})
export class ContentListComponent implements OnInit {
  newContentOptions = [
    {
      label: 'Video',
      icon: '../../../assets/images/icons/video-content.svg',
      url: '/contents/video/new',
    },
    {
      label: 'Activity',
      icon: '../../../assets/images/icons/activity-content.svg',
      url: '/contents/activity/new',
    },
    {
      label: 'Game',
      icon: '../../../assets/images/icons/game-content.svg',
      url: '/contents/game/new',
    },
    {
      label: 'EDSW',
      icon: '../../../assets/images/icons/edsw-content.svg',
      url: '/contents/edsw/new',
    },
  ];

  headers = [
    { label: 'Preview', sortable: false },
    { label: 'Title', key: 'title', sortable: true },
    { label: 'Type', key: 'contentType', sortable: true },
    { label: 'Date', key: 'releaseDate', sortable: true },
    { label: 'Content Link' },
    { label: 'Status' },
    { label: 'Actions' },
  ];

  currentPage = 1;
  searchTerm = '';
  sorting = false;
  loading = false;
  deletingContentId: string | null = null;
  contentsList: ContentList | null = null;
  sortingParams: { key?: string; asc?: boolean } | null = null;
  isHovered: { [key: number]: boolean } = {};
  isSmallScreen = window.innerWidth < 1700;
  learningServiceStatus: 'pending' | 'success' | 'error' = 'pending';

  constructor(
    private router: Router,
    private authService: AuthService,
    private dialogService: DialogService,
    private contentService: ContentService,
    private http: HttpService
  ) {}

  ngOnInit(): void {
    this.loadList();
    // Check learning service health on init.
    this.http.get(environment.learningServiceBaseUrl + '/health/live').subscribe({
      next: response => {
        this.learningServiceStatus = 'success';
        console.log(`Learning service connection + health check: ${response.status == 'ok' ? 'success' : 'failure'}`);
      },
      error: () => (this.learningServiceStatus = 'error'),
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.isSmallScreen = window.innerWidth < 1700;
  }

  loadList(hideLoader?: boolean): void {
    var params: ListParams = { page: this.currentPage };
    if (this.searchTerm) {
      params.text = this.searchTerm;
    }

    if (this.sortingParams) {
      params.orderBy = this.sortingParams.key;
      params.sort = this.sortingParams.asc ? 'asc' : 'desc';
    }

    if (!hideLoader) {
      this.loading = true;
    }

    this.contentService.list(params).subscribe(
      response => {
        this.sorting = false;
        this.loading = false;
        response.contents.forEach(c => {
          c.releaseDate = new Date(c.releaseDate).toLocaleDateString('en-US');
        });
        this.contentsList = response;
      },
      errors => {
        this.sorting = false;
        this.loading = false;
        this.dialogService.showErrorAlert(formatError(errors));
        if (errors.status === 401 || errors.error?.code === 'error-auth-session' || errors.error?.code === 'session-validate-client') {
          this.authService.clearSession();
          this.router.navigate(['/login']);
        }
      }
    );
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.loadList();
  }

  onSearchChange(term: string) {
    this.searchTerm = term;
    this.currentPage = 1;
    this.loadList();
  }

  onSortChange(params: { key: string; asc: boolean }) {
    this.sorting = true;
    this.sortingParams = params;
    this.loadList(true);
  }

  delete(contentId: string) {
    this.deletingContentId = contentId;
    this.contentService.delete(contentId).subscribe(() => {
      this.deletingContentId = null;
      this.dialogService.showSuccessAlert('Delete Successful.');
      this.loadList();
    });
  }

  confirmDelete(contentId: string, contentTitle: string) {
    this.dialogService.showConfirmationDialog({
      type: 'danger',
      confirmButtonLabel: 'Delete',
      onConfirmCallback: () => this.delete(contentId),
      dialogIconUrl: '../../../../assets/images/icons/alert-circle.svg',
      confirmButtonIconUrl: '../../../../assets/images/icons/delete.svg',
      title: 'Are you sure you want to remove ' + contentTitle + ' content from the Curriculum?',
      message: 'This action cannot be undone.',
    });
  }

  toggleHover(index: number) {
    this.isHovered[index] = !this.isHovered[index];
  }
}
